/**
 * **Souffler** — les remèdes de santé mentale, qui n'avaient aucun écran.
 *
 * La santé mentale est un **game over** (PLAN §4.10) et ses trois remèdes existent dans le
 * moteur depuis M1 — `pause`, `therapie`, `annoncer_pause` ⟦A-2⟧ — sans qu'un seul bouton du
 * client ne permette de les jouer. Le mode découverte ⟦A-9⟧ rend ce trou intenable : sa fiche
 * « les commentaires et la santé mentale » promet des remèdes, il faut donc qu'ils soient là.
 * « Un bouton qui marche vaut mieux qu'un trou » — précédent : le `CurseurStub` des mini-jeux.
 *
 * Trois règles tenues ici, et aucune n'est réinventée :
 *
 * - **Les coûts viennent de `actionCost`** (via `coutAction`, qui lit les réglages du contenu), jamais d'une table recopiée : ils sont ✎ et la
 *   passe A-9 vient déjà d'en doubler une partie.
 * - **La Pause annoncée reste semi-cachée** (A-2) : tant que `pauseDecouverte` est faux, elle
 *   n'apparaît pas du tout. Pas de bouton grisé qui vend la mèche — c'est exactement ce que
 *   l'avenant refuse.
 * - **Le panneau ne s'ouvre pas de nulle part** : il se propose depuis le bandeau de santé
 *   mentale, quand la jauge quitte le vert. C'est la « pédagogie par le besoin » d'A-2, et ça
 *   préserve le départ à deux boutons d'⟦A-5⟧.
 *
 * ## ⟦C57 · lot 5a · A-174⟧ Moins de texte : une cure, c'est un nom, une phrase, un coût, un bouton
 *
 * Mesuré avant le chantier : 113 mots visibles, dont deux phrases de 27 et 25 mots. À l'écran :
 * l'état de la santé mentale, puis par carte son titre, UNE phrase de 12 mots au plus, le coût en
 * icônes et le bouton. Le texte de chaque cure (le mot pour mot d'avant) est derrière le
 * pictogramme 📰 de sa carte : **rien n'est supprimé, rien n'est réécrit**. Les confirmations
 * tiennent en deux phrases courtes ; ce qu'elles ne disent plus est dans les dessous de la carte.
 */

import {
  ANNONCE_HEURES,
  SM_NUIT,
  argentOuvert,
  pauseDecouverte,
  pausesAnnoncees,
  plafondMomentum,
  type Action,
} from "@maxime/shared";

import { DessousBloc, LesDessous } from "../../components/LesDessous";
import { Panneau } from "../../components/Panneau";
import { contenu } from "../../game/content";
import { libelleCout, zoneSM } from "../../game/format";
import { coutAction } from "../../game/menage";
import { useRunStore } from "../../stores/runStore";
import { dialog } from "../../stores/uiStore";

export function Souffler() {
  const run = useRunStore((s) => s.run);
  const act = useRunStore((s) => s.act);
  const pending = useRunStore((s) => s.pending);

  if (!run) return null;
  const state = run.state;
  // ⟦C60 bis · A-177⟧ Le coût se lit avec les réglages du contenu (la thérapie ne se paie plus en Clicks).
  const cout = (action: Action) => libelleCout(coutAction(action, state));
  const zone = zoneSM(state.mental);

  async function jouer(action: Action, titre: string, corps: string, libelle: string) {
    const ok = await dialog.confirm({ title: titre, body: corps, confirmLabel: libelle });
    if (ok) await act(action);
  }

  // ⟦A-29⟧ « Ce qui coûte de l'argent est dans le marché. » La thérapie coûte des Clicks : avant
  // le Grand Saut elle n'est pas chère, elle **n'existe pas** — le moteur la refuse désormais en
  // `mecanique_verrouillee`, et l'écran suit la règle d'⟦A-5⟧ (« ce qui n'est pas déverrouillé
  // est absent, pas grisé »), comme les Jalons et la Bibliothèque. Un bouton gris pendant tout
  // l'arc 1 disait au joueur qu'il lui manquait quelque chose ; il ne lui manque rien : dans
  // l'arc 1, ce qui remonte la santé mentale, c'est le sommeil, et c'est voulu.
  //
  // La porte est lue dans le moteur (`argentOuvert`), jamais recalculée ici.
  const clicksOuverts = argentOuvert(state);
  // ⟦C60 bis · A-177⟧ « Les Clicks ne paient plus que le Marché et l'Équipe » (Matt, 03/10/2026) : la séance se paie en
  // TEMPS et en ÉNERGIE. Elle est payable quand l'énergie le permet (et les Clicks, si le contenu en demande encore :
  // c'est le même `coutAction` que le moteur débitera, jamais une table recopiée ici).
  const coutTherapie = coutAction({ type: "therapie" }, state);
  const therapiePayable =
    state.money >= -coutTherapie.money_delta && state.energy >= -coutTherapie.energy_delta;
  const therapieEnClicks = coutTherapie.money_delta < 0;
  // Le moteur refuse déjà une seconde annonce (`pause_non_decouverte` / `deja_en_pause`).
  const dejaEnPause = state.pause_announced != null;

  // ⟦C57 · A-174⟧ Le texte d'avant, mot pour mot : il part derrière le 📰 de la carte.
  const texteNuit = clicksOuverts
    ? "Le remède de base, et le seul gratuit. Il est sur ton lit, et dans la barre d'actions — il ne coûte que ta journée."
    : // ⟦A-29⟧ Dans l'arc 1, ce n'est pas « le remède de base » : c'est LE remède, et
      // c'est voulu (voir `SM_NUIT`, actions.ts — « un débutant épuisé ne va pas en
      // thérapie, il dort »). Le joueur doit le savoir, sinon il cherche une porte
      // qui n'existe pas encore.
      `Tant que tu n'as pas d'argent à toi, c'est ton seul remède. Une vraie nuit dans ton lit rend ${SM_NUIT} de santé mentale, et elle ne coûte que ta journée. Le lit est dans la chambre, et « Dormir » est dans la barre d'actions.`;

  return (
    <Panneau titre="🫖 Souffler">
      <p className="ligne-info">
        Santé mentale : <strong>{Math.round(state.mental)}</strong> ·{" "}
        {zone === "vert"
          ? "tu tiens le rythme."
          : zone === "fatigue"
            ? "fatigué — ce que tu produis en prend un coup."
            : "zone rouge. À zéro, la chaîne s'arrête pour de bon."}
      </p>

      <section className="carte">
        <div className="l5-tete">
          <h2 className="bloc-titre">😴 Une bonne nuit</h2>
          <LesDessous titre="Une bonne nuit" variante="icone">
            <DessousBloc>
              <p className="dessous-texte">{texteNuit}</p>
            </DessousBloc>
          </LesDessous>
        </div>
        <p className="ligne-info">
          {clicksOuverts
            ? "Le remède gratuit : il ne coûte que ta journée."
            : `Ton seul remède : +${SM_NUIT} santé mentale par nuit.`}
        </p>
      </section>

      <section className="carte">
        <div className="l5-tete">
          <h2 className="bloc-titre">🌴 Prendre une vraie pause</h2>
          <LesDessous titre="Prendre une vraie pause" variante="icone">
            <DessousBloc>
              <p className="dessous-texte">
                Une semaine entière hors ligne. Tu reviens reposé — mais tu reviens aussi à une chaîne
                qui a continué sans toi : le silence non annoncé, ça se paie en abonnés.
              </p>
            </DessousBloc>
            <DessousBloc titre="Si tu coupes">
              <p className="dessous-texte">
                Tu disparais pendant une semaine simulée. Tu récupères beaucoup d'énergie et de santé
                mentale — et ton public, lui, ne sait pas où tu es passé.
              </p>
            </DessousBloc>
          </LesDessous>
        </div>
        <p className="ligne-info">Hors ligne une semaine : repos, mais le silence se paie.</p>
        <button
          className="bouton primaire"
          type="button"
          disabled={pending}
          onClick={() =>
            void jouer(
              { type: "pause" },
              "Couper une semaine ?",
              // ⟦C57 · A-174⟧ Deux phrases courtes ; le texte d'avant est dans les dessous de la carte.
              "Énergie et santé mentale remontent. Ton public ne sait pas où tu es.",
              "Je coupe",
            )
          }
        >
          Couper une semaine
          <span className="cout">{cout({ type: "pause" })}</span>
        </button>
      </section>

      {/* ⟦A-2⟧ Semi-cachée : absente tant qu'elle n'est pas découverte (1er passage sous 30,
          ou le nœud dédié). Le moteur la refuse de toute façon (`pause_non_decouverte`). */}
      {pauseDecouverte(state, contenu()) ? (
        <section className="carte">
          <div className="l5-tete">
            <h2 className="bloc-titre">📢 Annoncer la pause</h2>
            <LesDessous titre="Annoncer la pause" variante="icone">
              <DessousBloc>
                <p className="dessous-texte">
                  Tu tournes un message pour prévenir ta communauté avant de partir. La pause ne
                  commence qu'à ton prochain sommeil — annoncer puis continuer à publier n'ouvre
                  rien. Bien annoncée, elle ne te punit plus, et ton retour fait un événement.
                </p>
              </DessousBloc>
              <DessousBloc titre="Où tu en es">
                <p className="dessous-texte">
                  {pausesAnnoncees(state)} pause(s) annoncée(s) sur cette run · plafond de momentum
                  actuel ×{plafondMomentum(state).toFixed(2).replace(".", ",")}.
                </p>
              </DessousBloc>
              <DessousBloc titre="Si tu annonces">
                <p className="dessous-texte">
                  Tu passes {ANNONCE_HEURES} h à tourner un message. La pause démarrera à ton
                  prochain sommeil, et le silence ne te sera pas reproché.
                </p>
              </DessousBloc>
            </LesDessous>
          </div>
          <p className="ligne-info">Prévenir ta communauté : la pause ne te punit plus.</p>
          <p className="ligne-info discret">
            {pausesAnnoncees(state)} annoncée(s) · momentum ×{plafondMomentum(state).toFixed(2).replace(".", ",")}
          </p>
          <button
            className="bouton primaire"
            type="button"
            disabled={pending || dejaEnPause}
            onClick={() =>
              void jouer(
                { type: "annoncer_pause" },
                "Prévenir ta communauté ?",
                `Un message de ${ANNONCE_HEURES} h. La pause démarre à ton prochain sommeil.`,
                "J'annonce",
              )
            }
          >
            {dejaEnPause ? "Pause déjà annoncée" : "Annoncer une pause"}
            <span className="cout">{cout({ type: "annoncer_pause" })}</span>
          </button>
        </section>
      ) : null}

      {/* ⟦A-29⟧ Absente avant le Grand Saut — voir `clicksOuverts` plus haut. */}
      {clicksOuverts ? (
        <section className="carte">
          <div className="l5-tete">
            <h2 className="bloc-titre">🛋 Voir quelqu'un</h2>
            <LesDessous titre="Voir quelqu'un" variante="icone">
              <DessousBloc>
                <p className="dessous-texte">
                  {therapieEnClicks
                    ? "Une séance. Ça remonte la santé mentale sans coûter une semaine — mais ça se paie en Clicks, et personne n'a jamais fait une carrière en n'y allant qu'une fois."
                    : "Une séance. Ça remonte la santé mentale sans coûter une semaine — mais ça prend du temps et ça vide l'énergie : on n'y va pas pour se reposer, on y va pour aller mieux."}
                </p>
              </DessousBloc>
            </LesDessous>
          </div>
          <p className="ligne-info">
            {therapieEnClicks
              ? "Une séance, payée en Clicks : la santé mentale remonte."
              : "Une séance, payée en temps et en énergie : la santé mentale remonte."}
          </p>
          <button
            className="bouton"
            type="button"
            disabled={pending || !therapiePayable}
            onClick={() =>
              void jouer(
                { type: "therapie" },
                "Prendre rendez-vous ?",
                therapieEnClicks
                  ? "Une séance, une facture, et une vraie remontée de santé mentale."
                  : "Une séance, du temps, de l'énergie : et une vraie remontée de santé mentale.",
                "J'y vais",
              )
            }
          >
            Prendre rendez-vous
            <span className="cout">{cout({ type: "therapie" })}</span>
          </button>
        </section>
      ) : null}
    </Panneau>
  );
}
